import { X } from 'lucide-react';
import { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export type ReportType = 'profile' | 'photo' | 'chat';
export type ReportSubType = 'profile' | 'photo';

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { reason: string; entityType: number; entityId: string }) => Promise<void>;
  reportType: ReportType;
  userName?: string;
  profileImageId?: string;
}

const ReportModal: React.FC<ReportModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  reportType,
  userName = 'this user',
  profileImageId = '',
}) => {
  const [reportReason, setReportReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [selectedSubType, setSelectedSubType] = useState<ReportSubType>('profile');

  if (!isOpen) return null;

  const getTitle = () => {
    if (reportType === 'profile') {
      return 'Report User';
    }
    switch (reportType) {
      case 'photo':
        return 'Report Profile Image';
      case 'chat':
        return 'Report Chat';
      default:
        return 'Report';
    }
  };

  const getDescription = () => {
    if (reportType === 'profile') {
      return `You are reporting ${userName}. Please select what you want to report and provide a reason.`;
    }
    switch (reportType) {
      case 'photo':
        return `You are reporting ${userName}'s profile image. Please provide a reason.`;
      case 'chat':
        return `You are reporting chat messages with ${userName}. Please provide a reason.`;
      default:
        return 'Please provide a reason for your report.';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!reportReason.trim()) {
      setError('Please provide a reason for the report');
      return;
    }

    setError('');
    setIsSubmitting(true);

    try {
      let entityType: number;
      let entityId = '';

      if (reportType === 'profile') {
        if (selectedSubType === 'photo') {
          entityType = 2; 
          entityId = profileImageId;
        } else {
          entityType = 4; 
        }
      } else if (reportType === 'photo') {
        entityType = 2; 
        entityId = profileImageId;
      } else {
        entityType = 1; 
      }

      await onSubmit({
        reason: reportReason.trim(),
        entityType,
        entityId,
      });
      setReportReason('');
      setSelectedSubType('profile');
      onClose();
    } catch (err) {
      setError('Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setReportReason('');
      setSelectedSubType('profile');
      setError('');
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">{getTitle()}</h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <p className="text-sm text-gray-600 mb-4">{getDescription()}</p>

          {reportType === 'profile' && (
            <div className="mb-4">
              <label htmlFor="reportSubType" className="block text-sm font-medium text-gray-700 mb-2">
                What would you like to report? <span className="text-red-500">*</span>
              </label>
              <Select
                value={selectedSubType}
                onValueChange={(value) => setSelectedSubType(value as ReportSubType)}
                disabled={isSubmitting}
              >
                <SelectTrigger 
                  className="w-full bg-white"
                  style={{
                    border: '1px solid rgb(209, 213, 219)',
                    borderRadius: '0.5rem'
                  }}
                >
                  <SelectValue placeholder="Select report type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="profile">Profile</SelectItem>
                  <SelectItem value="photo">Profile Image</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="mb-4">
            <label htmlFor="reportReason" className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Report <span className="text-red-500">*</span>
            </label>
            <textarea
              id="reportReason"
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              placeholder="Please describe why you are reporting..."
              rows={5}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
              maxLength={500}
            />
            <p className="text-xs text-gray-500 mt-1">
              {reportReason.length}/500 characters
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !reportReason.trim()}
              className="flex-1 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReportModal;
